import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import { useHistory } from 'react-router-dom'
import useSWR from 'swr'
import { fetcher } from '../../utils/api'
import Spinner from '../Spinner'
import { shareMarketplaceItemLink } from '../../utils/marketplace'
import { ErrorNotification, SuccessNotification } from '../../utils'

const ServiceDetailModal = ({
  show,
  setShow,
  service
}) => {
  const history = useHistory()
  const { data, isLoading, mutate } = useSWR(`service-orders/products/${service?.id}/`, fetcher)

  const handleShareMarketplaceLink = async () => {
    if (!data?.is_active) {
      ErrorNotification('Enable visibility on this service before sharing its marketplace link.')
      return
    }

    await shareMarketplaceItemLink(data, {
      onCopied: () => SuccessNotification('Marketplace link copied to clipboard'),
      onError: (message) => ErrorNotification(message),
    })
  }

  const details = useMemo(() => {
    if (isLoading) return []

    return [
      { label: 'Service Name', value: data?.name },
      { label: 'Service Type', value: data?.item_type === 'service' ? 'Service' : 'Product' },
      { label: 'Service Price', value: data?.unit_price ? `GHC ${parseFloat(data.unit_price).toFixed(2)}` : 'Not specified' },
      { label: 'Service Visibility', value: data?.is_active ? 'On' : 'Off', isStatus: true },
      { label: 'Country', value: data?.country },
      { label: 'Region/State', value: data?.region },
      { label: 'Community', value: data?.community },
      { label: 'Open for Negotiation', value: data?.is_negotiable ? 'Yes' : 'No' },
    ]
  }, [data])

  const getCategoryDisplay = () => {
    if (!data?.categories || data.categories.length === 0) return null

    const mainCategories = data.categories.filter(cat => cat.is_main_category)
    const subCategories = data.categories.filter(cat => cat.is_subcategory)

    return (
      <div className="mb-4">
        <div className="mb-2 text-sm font-medium text-black dark:text-white">Service Categories</div>
        <div className="space-y-2">
          {mainCategories.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Main Categories:</div>
              <div className="flex flex-wrap gap-2">
                {mainCategories.map((category, idx) => (
                  <span key={idx} className="px-3 py-1 bg-yellowcolor text-black rounded-full text-sm font-medium">
                    {category.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {subCategories.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Sub Categories:</div>
              <div className="flex flex-wrap gap-2">
                {subCategories.map((category, idx) => (
                  <span key={idx} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full text-sm font-medium">
                    {category.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  const getDeliveryDisplay = (field, label) => {
    if (!data?.[field] || data[field].length === 0) return null

    return (
      <div className="mb-4">
        <div className="mb-2 text-sm font-medium text-black dark:text-white">{label}</div>
        <div className="flex flex-wrap gap-2">
          {data[field].map((item, idx) => (
            <span key={idx} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full text-sm font-medium">
              {item}
            </span>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full md:max-w-4xl relative max-h-[90vh] flex flex-col">
        <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark'>
          <h2 className='text-xl font-bold dark:text-white'>
            Service Details
          </h2>
          <button
            onClick={() => setShow(false)}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-5 w-5" />
          </button>
        </div>

        {isLoading ? <div className="flex justify-center items-center h-full py-10">
          <Spinner />
        </div> : <div className="flex-1 overflow-y-auto p-6">

          {/* Service Details Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 text-yellowcolor">Service Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {details.map((item, idx) => (
                <div key={idx} className="flex flex-col gap-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{item.label}</span>
                  <span className={`font-semibold text-black dark:text-white text-base ${item.isStatus && item.value === 'On' ? 'text-yellowcolor' : ''
                    }`}>
                    {item.value || 'Not specified'}
                  </span>
                </div>
              ))}
            </div>

            {/* Service Categories - Special Display */}
            {getCategoryDisplay()}

            {/* Description */}
            <div className="mt-6 col-span-2">
              <div className="mb-2 text-sm font-medium text-black dark:text-white">Service Description</div>
              <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                {data?.description || 'No description available'}
              </div>
            </div>
          </div>

          {/* Delivery Locations Section */}
          {(data?.delivery_countries?.length > 0 || data?.delivery_regions?.length > 0 || data?.delivery_communities?.length > 0) && (
            <>
              <hr className='my-8 border-gray-200 dark:border-border-dark' />
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 text-yellowcolor">Service Delivery Locations</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {getDeliveryDisplay('delivery_countries', 'Countries')}
                  {getDeliveryDisplay('delivery_regions', 'States/Regions')}
                  {getDeliveryDisplay('delivery_communities', 'Communities')}
                </div>
              </div>
            </>
          )}

          {/* Service Images Section */}
          <hr className='my-8 border-gray-200 dark:border-border-dark' />
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 text-yellowcolor">Service Images</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {data?.images?.map((img, idx) => (
                <div key={idx} className="w-full aspect-square border-2 border-gray-200 dark:border-border-dark rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-700">
                  {img?.image && (
                    <img
                      src={img.image}
                      alt="service"
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              ))}
              {/* Fill empty cells if less than 4 images */}
              {Array.from({ length: Math.max(0, 4 - (data?.images?.length || 0)) }).map((_, idx) => (
                <div key={`empty-${idx}`} className="w-full aspect-square border-2 border-dashed border-gray-200 dark:border-border-dark rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
                  <span className="text-gray-400 text-sm">No Image</span>
                </div>
              ))}
            </div>
          </div>

          {/* Created At */}
          <div className="mt-6">
            <div className="mb-2 text-sm font-medium text-black dark:text-white">Created At</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              {dayjs(data?.created_at || service?.created_at).format('MMMM D, YYYY h:mm A')}
            </div>
          </div>
        </div>}

        <div className='border-t p-4 z-50 border-gray-200 flex flex-row items-end justify-end w-full bg-white dark:bg-dark flex-shrink-0 dark:border-border-dark'>
          <div className="flex flex-row items-end justify-end gap-4">
            <button
              onClick={() => setShow(false)}
              className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg hover:bg-gray-50'>
              Close
            </button>

            {data?.id && (
              <>
                <button
                  type="button"
                  onClick={handleShareMarketplaceLink}
                  disabled={!data?.is_active}
                  className="px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Share link
                </button>
                <button
                  type="button"
                  onClick={() => history.push("/admin/edit-service", {
                    state: {
                      ...data
                    }
                  })}
                  className="px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg font-semibold hover:bg-yellow-400"
                >
                  Edit Service
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ServiceDetailModal
